import React, { useState, useEffect } from 'react';
import { Shield, Users, GitBranch, Settings, Check, X, AlertTriangle, Eye, Lock, Unlock, ChevronDown, ChevronUp, Plus, Loader2 } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';

interface Team {
    id: string;
    name: string;
    memberCount: number;
}

interface DataScope {
    id: string;
    name: string;
    description: string;
    sensitive: boolean;
}

interface Permission {
    teamId: string;
    scopeId: string;
    canRead: boolean;
    canWrite: boolean;
    canExport: boolean;
}

interface DataPermissionsViewProps {
    onNavigate?: (view: string) => void;
}

const defaultTeams: Team[] = [
    { id: 't1', name: 'Sales Team', memberCount: 5 },
    { id: 't2', name: 'Marketing', memberCount: 3 },
    { id: 't3', name: 'Analytics', memberCount: 4 },
];

const defaultDataScopes: DataScope[] = [
    { id: 's1', name: 'Customer Data', description: 'Customer contact info and history', sensitive: true },
    { id: 's2', name: 'Financial Data', description: 'Invoices, payments, pricing', sensitive: true },
    { id: 's3', name: 'Product Catalog', description: 'Product info and inventory', sensitive: false },
    { id: 's4', name: 'Marketing Assets', description: 'Campaigns, content, analytics', sensitive: false },
    { id: 's5', name: 'HR Records', description: 'Employee data and policies', sensitive: true },
];

const DataPermissionsView: React.FC<DataPermissionsViewProps> = ({ onNavigate }) => {
    const { user } = useAuth();
    const [teams, setTeams] = useState<Team[]>([]);
    const [dataScopes, setDataScopes] = useState<DataScope[]>([]);
    const [permissions, setPermissions] = useState<Permission[]>([]);
    const [expandedTeam, setExpandedTeam] = useState<string | null>(null);
    const [showAuditWarning, setShowAuditWarning] = useState(false);
    const [pendingChange, setPendingChange] = useState<{ teamId: string, scopeId: string, field: keyof Permission, value: boolean } | null>(null);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);

    const firestoreDocRef = user ? doc(db, 'users', user.uid, 'settings', 'permissions') : null;

    // Load permissions from Firestore
    useEffect(() => {
        if (!firestoreDocRef) {
            setLoading(false);
            return;
        }

        const loadData = async () => {
            try {
                const snap = await getDoc(firestoreDocRef);
                if (snap.exists()) {
                    const data = snap.data();
                    setTeams(data.teams || defaultTeams);
                    setDataScopes(data.dataScopes || defaultDataScopes);
                    setPermissions(data.permissions || []);
                } else {
                    // First time: use defaults
                    setTeams(defaultTeams);
                    setDataScopes(defaultDataScopes);
                    setPermissions([]);
                }
            } catch {
                // Firestore unavailable, use defaults
                setTeams(defaultTeams);
                setDataScopes(defaultDataScopes);
                setPermissions([]);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [user?.uid]);

    // Save to Firestore when permissions change
    const saveToFirestore = async (newPermissions: Permission[]) => {
        if (!firestoreDocRef) return;
        setSaving(true);
        try {
            await setDoc(firestoreDocRef, {
                teams,
                dataScopes,
                permissions: newPermissions,
                updatedAt: new Date().toISOString(),
            }, { merge: true });
        } catch {
            // Silently fail — permissions still work locally
        } finally {
            setSaving(false);
        }
    };

    const getPermission = (teamId: string, scopeId: string): Permission | undefined => {
        return permissions.find(p => p.teamId === teamId && p.scopeId === scopeId);
    };

    const updatePermission = (teamId: string, scopeId: string, field: keyof Permission, value: boolean) => {
        const scope = dataScopes.find(s => s.id === scopeId);

        // Show warning for sensitive data changes
        if (scope?.sensitive && value) {
            setPendingChange({ teamId, scopeId, field, value });
            setShowAuditWarning(true);
            return;
        }

        applyPermissionChange(teamId, scopeId, field, value);
    };

    const applyPermissionChange = (teamId: string, scopeId: string, field: keyof Permission, value: boolean) => {
        setPermissions(prev => {
            const existing = prev.findIndex(p => p.teamId === teamId && p.scopeId === scopeId);
            let updated: Permission[];
            if (existing >= 0) {
                updated = [...prev];
                updated[existing] = { ...updated[existing], [field]: value };
            } else {
                updated = [...prev, { teamId, scopeId, canRead: false, canWrite: false, canExport: false, [field]: value }];
            }
            saveToFirestore(updated);
            return updated;
        });
    };

    const confirmPendingChange = () => {
        if (pendingChange) {
            applyPermissionChange(pendingChange.teamId, pendingChange.scopeId, pendingChange.field, pendingChange.value);
            setPendingChange(null);
        }
        setShowAuditWarning(false);
    };

    const PermissionToggle: React.FC<{
        value: boolean;
        onChange: (v: boolean) => void;
        label: string;
        disabled?: boolean;
    }> = ({ value, onChange, label, disabled }) => (
        <button
            onClick={() => !disabled && onChange(!value)}
            disabled={disabled}
            className={`flex items-center gap-1.5 px-2 py-1 rounded text-xs font-medium transition-colors ${value
                    ? 'bg-hig-success/10 text-hig-success border border-hig-success/20'
                    : 'bg-hig-bg-tertiary text-hig-text-tertiary border border-transparent hover:border-hig-border'
                } ${disabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
        >
            {value ? <Check size={12} /> : <X size={12} />}
            {label}
        </button>
    );

    if (loading) {
        return (
            <div className="flex items-center justify-center h-64">
                <Loader2 size={32} className="animate-spin text-hig-accent" />
            </div>
        );
    }

    return (
        <div className="space-y-8">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-semibold text-hig-text-primary flex items-center gap-3">
                        <Shield size={24} className="text-hig-accent" />
                        Data Permissions
                    </h1>
                    <p className="text-hig-text-secondary mt-1">
                        Control what data each team and their AI replicas can access
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {saving && <span className="text-xs text-hig-text-tertiary">Saving...</span>}
                    <button className="btn-primary flex items-center gap-2">
                        <Settings size={16} />
                        Permission Templates
                    </button>
                </div>
            </div>

            {/* Quick Stats */}
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                <div className="card p-4">
                    <p className="text-2xl font-bold text-hig-text-primary">{teams.length}</p>
                    <p className="text-sm text-hig-text-tertiary">Teams</p>
                </div>
                <div className="card p-4">
                    <p className="text-2xl font-bold text-hig-text-primary">{dataScopes.length}</p>
                    <p className="text-sm text-hig-text-tertiary">Data Scopes</p>
                </div>
                <div className="card p-4">
                    <p className="text-2xl font-bold text-hig-warning">{dataScopes.filter(s => s.sensitive).length}</p>
                    <p className="text-sm text-hig-text-tertiary">Sensitive Scopes</p>
                </div>
                <div className="card p-4">
                    <p className="text-2xl font-bold text-hig-success">{permissions.length}</p>
                    <p className="text-sm text-hig-text-tertiary">Active Permissions</p>
                </div>
            </div>

            {/* Permission Matrix by Team */}
            <div className="space-y-4">
                <h2 className="text-lg font-semibold text-hig-text-primary flex items-center gap-2">
                    <Users size={18} />
                    Permissions by Team
                </h2>

                {teams.map((team) => (
                    <div key={team.id} className="card overflow-hidden">
                        <button
                            onClick={() => setExpandedTeam(expandedTeam === team.id ? null : team.id)}
                            className="w-full p-4 flex items-center justify-between hover:bg-hig-bg-tertiary/50 transition-colors"
                        >
                            <div className="flex items-center gap-3">
                                <div className="w-10 h-10 rounded-lg bg-hig-accent/10 flex items-center justify-center">
                                    <GitBranch size={18} className="text-hig-accent" />
                                </div>
                                <div className="text-left">
                                    <p className="font-medium text-hig-text-primary">{team.name}</p>
                                    <p className="text-xs text-hig-text-tertiary">{team.memberCount} members (including AI replicas)</p>
                                </div>
                            </div>
                            <div className="flex items-center gap-4">
                                <span className="text-sm text-hig-text-tertiary">
                                    {permissions.filter(p => p.teamId === team.id).length} scopes configured
                                </span>
                                {expandedTeam === team.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                            </div>
                        </button>

                        {expandedTeam === team.id && (
                            <div className="border-t border-hig-divider">
                                <div className="overflow-x-auto">
                                    <table className="w-full">
                                        <thead className="bg-hig-bg-tertiary">
                                            <tr>
                                                <th className="px-4 py-3 text-left text-xs font-medium text-hig-text-tertiary uppercase">Data Scope</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-hig-text-tertiary uppercase">Read</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-hig-text-tertiary uppercase">Write</th>
                                                <th className="px-4 py-3 text-center text-xs font-medium text-hig-text-tertiary uppercase">Export</th>
                                            </tr>
                                        </thead>
                                        <tbody className="divide-y divide-hig-divider">
                                            {dataScopes.map((scope) => {
                                                const perm = getPermission(team.id, scope.id);
                                                return (
                                                    <tr key={scope.id} className="hover:bg-hig-bg-tertiary/30">
                                                        <td className="px-4 py-3">
                                                            <div className="flex items-center gap-2">
                                                                {scope.sensitive ? (
                                                                    <Lock size={14} className="text-hig-warning" />
                                                                ) : (
                                                                    <Unlock size={14} className="text-hig-text-tertiary" />
                                                                )}
                                                                <div>
                                                                    <p className="font-medium text-hig-text-primary text-sm">{scope.name}</p>
                                                                    <p className="text-xs text-hig-text-tertiary">{scope.description}</p>
                                                                </div>
                                                            </div>
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <PermissionToggle
                                                                value={perm?.canRead || false}
                                                                onChange={(v) => updatePermission(team.id, scope.id, 'canRead', v)}
                                                                label="Read"
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <PermissionToggle
                                                                value={perm?.canWrite || false}
                                                                onChange={(v) => updatePermission(team.id, scope.id, 'canWrite', v)}
                                                                label="Write"
                                                                disabled={!perm?.canRead}
                                                            />
                                                        </td>
                                                        <td className="px-4 py-3 text-center">
                                                            <PermissionToggle
                                                                value={perm?.canExport || false}
                                                                onChange={(v) => updatePermission(team.id, scope.id, 'canExport', v)}
                                                                label="Export"
                                                                disabled={!perm?.canRead}
                                                            />
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                ))}
            </div>

            {/* Audit Notice */}
            <div className="card p-4 border-l-4 border-hig-accent">
                <div className="flex items-start gap-3">
                    <Eye size={18} className="text-hig-accent mt-0.5" />
                    <div>
                        <p className="font-medium text-hig-text-primary">All permission changes are audited</p>
                        <p className="text-sm text-hig-text-secondary">
                            Every modification to data permissions is logged in the audit trail. AI replicas cannot access data outside their assigned permissions.
                        </p>
                    </div>
                </div>
            </div>

            {/* Sensitive Data Warning Modal */}
            {showAuditWarning && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                    <div className="card p-6 max-w-md mx-4">
                        <div className="flex items-start gap-3 mb-4">
                            <div className="w-10 h-10 rounded-full bg-hig-warning/10 flex items-center justify-center flex-shrink-0">
                                <AlertTriangle size={20} className="text-hig-warning" />
                            </div>
                            <div>
                                <h3 className="text-lg font-semibold text-hig-text-primary">Granting Sensitive Data Access</h3>
                                <p className="text-sm text-hig-text-secondary mt-1">
                                    You are granting access to sensitive data. This action will be logged in the audit trail and may require additional approval based on your organization's policies.
                                </p>
                            </div>
                        </div>
                        <div className="flex gap-3 justify-end">
                            <button
                                onClick={() => {
                                    setPendingChange(null);
                                    setShowAuditWarning(false);
                                }}
                                className="btn-secondary"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={confirmPendingChange}
                                className="btn-primary"
                            >
                                Confirm & Log
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default DataPermissionsView;
